<template>
    <div>
      <ShowListingLicenseModal 
        v-if="licenseData"
        :show="showListingLicenseModal" 
        :licenseData="licenseData" 
        @close="closeListingLicenseModal" 
      />
      <div class="dashboard-grid">
        <div class="sales-summary">
          <!-- Header com título e total de vendas -->
          <div class="sales-summary-header">
            <div class="header-content flex justify-between items-center">
              <div>
                <h2 class="sales-summary-title">Sales Report</h2>
                <p class="sales-summary-subtitle">
                  Complete overview of your sales performance
                </p>
              </div>
  
              <div class="header-stats">
                <span class="sales-summary-count">{{ balance.totalSales }}</span>
                <span class="sales-summary-label">Total Sales</span>
              </div>
            </div>
          </div>
  
          <div class="stripe-section" v-if="balance.stripeSales > 0">
            <div class="metric-card">
              <div class="metric-content">
                <div class="metric-header">
                  <span class="metric-title">Stripe (USD)</span>
                  <span class="metric-count"
                    >{{ balance.stripeSales }} sales</span
                  >
                </div>
                <div class="metric-stats">
                  <div class="stat-row">
                    <span class="stat-label">Revenue</span>
                    <span class="stat-value stripe-value"
                      >${{ balance.stripeRevenue }}</span
                    >
                  </div>
                  <div class="stat-row">
                    <span class="stat-label">Avg. Sale</span>
                    <span class="stat-value stripe-value"
                      >${{
                        (balance.stripeRevenue / balance.stripeSales).toFixed(2)
                      }}</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
  
          <div class="wallet-total-section" v-if="balance.totalSales > 0">
            <div class="metric-card wallet-card" v-if="balance.walletSales > 0">
              <div class="metric-content">
                <div class="metric-header">
                  <span class="metric-title">Wallet (USD)</span>
                  <span class="metric-count"
                    >{{ balance.walletSales }} sales</span
                  >
                </div>
                <div class="metric-stats">
                  <div class="stat-row">
                    <span class="stat-label">Revenue</span>
                    <span class="stat-value wallet-value"
                      >{{ balance.walletRevenue }} USD</span
                    >
                  </div>
                  <div class="stat-row">
                    <span class="stat-label">Avg. Sale</span>
                    <span class="stat-value wallet-value"
                      >{{
                        (balance.walletRevenue / balance.walletSales).toFixed(2)
                      }}
                      USDC</span
                    >
                  </div>
                </div>
              </div>
            </div>
  
            <div class="total-summary">
              <div class="total-summary-header">
                <span class="total-summary-title">Total Revenue</span>
              </div>
              <div class="total-summary-value">
                <span v-if="balance.stripeRevenue > 0" class="total-amount"
                  >${{ balance.stripeRevenue }}</span
                >
                <span
                  v-if="balance.stripeRevenue > 0 && balance.walletRevenue > 0"
                  class="total-separator"
                >
                  +
                </span>
                <span v-if="balance.walletRevenue > 0" class="total-amount"
                  >{{ balance.walletRevenue }} USD</span
                >
              </div>
            </div>
          </div>
        </div>
  
        <div class="chart-container">
          <div class="chart-header">
            <h2 class="chart-title">Payment Methods Distribution</h2>
          </div>
          <div class="chart-content">
            <canvas ref="salesChart" class="sales-chart"></canvas>
          </div>
        </div>
      </div>
      <div class="receipt-details-section">
        <BaseCardTable
          :items="cardData"
          :quickFilterText="quickFilterText"
          class="md:hidden"
        />
        <SalesTable
          :data="paginatedData"
          :columns="columns"
          :totalItems="totalFilteredItems"
          :itemsPerPage="itemsPerPage"
          :currentPage="currentPage"
          :sortBy="sortBy"
          :sortOrder="sortOrder"
          :showSearch="true"
          :showPagination="true"
          :showItemsPerPage="true"
          :searchQuery="searchQuery"
          @update:searchQuery="handleSearchQueryUpdate"
          @update:currentPage="handlePageUpdate"
          @update:itemsPerPage="handleItemsPerPageUpdate"
          @sort="handleSort"
          title="Receipt Details"
          subtitle="Complete transaction history"
          class="hidden md:block"
        >
          <template #column-listing="{ item }">
            <router-link
              :to="`/item-detail/${item.listing_id}`"
              class="cursor-pointer font-semibold hover:text-echoes-primary transition-colors duration-200 w-fit truncate max-w-[200px]"
            >
              {{ item.listing }}
            </router-link>
          </template>
          <template #column-actions="{ item }">
            <div class="flex gap-2">
              <button class="view-license-button" @click="handleViewLicense(item)">
                  <i class="uil uil-file-alt text-lg"></i>
                  <span class="tooltip">View License</span>
              </button>
              <div v-if="item.credentials?.length > 0">
                <button
                  @click="openCredentialsModal(item.credentials[0].id)"
                  :disabled="!isCredentialsButtonEnabled(item)"
                  :class="[
                    'flex items-center gap-2 px-2.5 py-1 text-white rounded-md transition-colors duration-200',
                    isCredentialsButtonEnabled(item)
                      ? 'bg-echoes-primary hover:bg-echoes-primary/80'
                      : 'bg-echoes-background-gray opacity-50 cursor-not-allowed',
                  ]"
                >
                  <svg
                    class="w-5 h-5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                    <polyline points="10,17 15,12 10,7"></polyline>
                    <line x1="15" y1="12" x2="3" y2="12"></line>
                  </svg>
                  Credentials
                </button>
              </div>
              <div v-else>
                <button
                  class="flex items-center gap-2 px-2.5 py-1 text-white opacity-40 cursor-not-allowed"
                >
                  No Credentials
                </button>
              </div>
            </div>
          </template>
        </SalesTable>
      </div>
  
      <div
        v-if="credentialsModalOpen"
        class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] w-full"
      >
        <div
          class="bg-white dark:bg-echoes-background-gray rounded-lg p-6 max-w-7xl w-full mx-4"
        >
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              External Dataset Credentials
            </h3>
            <button
              @click="closeCredentialsModal"
              class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg
                class="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                ></path>
              </svg>
            </button>
          </div>
  
          <div class="grid grid-cols-4 gap-2">
            <div class="col-span-1 mt-6">
              <SaleHistoryCard :sale="saleHistory" />
            </div>
            <div class="col-span-3 flex flex-col gap-2">
              <div class="field-input-group-item-dataset-name w-full">
                <div class="field-input-group-item-label-container">
                  <div
                    class="field-input-group-item-label w-full text-center font-semibold text-white"
                  >
                    Credentials Instructions
                  </div>
                  <InputError
                    :message="errors.newCredentails"
                    class="label-error"
                  />
                </div>
                <div class="w-full max-h-[400px] overflow-y-auto">
                  <MarkdownRenderer
                    id="newCredentails"
                    v-model="newCredentails"
                    placeholder="Enter credentails instructions"
                    :required="false"
                    class="w-full"
                    @paste="handlePaste"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="flex justify-end mt-4 gap-2">
            <button
              @click="closeCredentialsModal"
              class="px-4 py-2 bg-echoes-primary text-white rounded-md hover:bg-echoes-primary/80"
            >
              Close
            </button>
            <button
              @click="updateCredentials"
              class="px-4 py-2 bg-echoes-primary text-white rounded-md hover:bg-echoes-primary/80"
            >
              Update Credential
            </button>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted, computed, nextTick } from "vue";
  import axios from "@/plugins/axios";
  import { Chart, registerables } from "chart.js";
  import SalesTable from "@/components/table/Table.vue";
  import BaseCardTable from "@/components/table/BaseCardTable.vue";
  import MarkdownRenderer from "@/components/MarkdownRenderer.vue";
  import { useToast } from "@/composables/use-toast";
  import { useStorage } from "@/composables/use-storage";
  import SaleHistoryCard from "@/components/cards/SaleHistoryCard.vue";
  import ShowListingLicenseModal from "@/pages/listing/showListingLicenseModal.vue";
  const { userInfo } = useStorage();
  
  const { addToast } = useToast();
  Chart.register(...registerables);
  
  const sales = ref([]);
  const rowData = ref([]);
  const cardData = ref([]);
  const salesChart = ref(null);
  let chartInstance = null;
  const quickFilterText = ref("");
  const loading = ref(false);
  const saleHistory = ref([]);
  
  // Search, pagination and sorting state
  const searchQuery = ref("");
  const sortBy = ref("created_at");
  const sortOrder = ref("desc");
  const currentPage = ref(1);
  const itemsPerPage = ref(10);
  const fetchSales = async () => {
    loading.value = true;
    const response = await axios.get(`/api/sales/my-sales`);
    sales.value = response.data;
  
    await nextTick();
    createChart();
    loading.value = false;
  };
  
  const createChart = () => {
    if (!salesChart.value || !sales.value.length) return;
  
    if (chartInstance) {
      chartInstance.destroy();
    }
  
    const ctx = salesChart.value.getContext("2d");
  
    const chartData = {
      labels: [],
      datasets: [
        {
          data: [],
          backgroundColor: ["#3B82F6", "#8B5CF6", "#10B981"],
          borderWidth: 2,
          borderColor: "#ffffff",
        },
      ],
    };
  
    if (balance.value.stripeSales > 0) {
      chartData.labels.push("💳 Stripe");
      chartData.datasets[0].data.push(balance.value.stripeSales);
    }
  
    if (balance.value.walletSales > 0) {
      chartData.labels.push("Wallet");
      chartData.datasets[0].data.push(balance.value.walletSales);
    }
  
    if (chartData.datasets[0].data.length === 0) {
      chartData.labels.push("No Sales");
      chartData.datasets[0].data.push(1);
      chartData.datasets[0].backgroundColor = ["#E5E7EB"];
    }
  
    chartInstance = new Chart(ctx, {
      type: "doughnut",
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "bottom",
            labels: {
              padding: 20,
              font: {
                size: 12,
              },
              color: "#6B7280",
            },
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                const percentage = ((context.parsed / total) * 100).toFixed(1);
                return `${context.label}: ${context.parsed} sales (${percentage}%)`;
              },
            },
          },
        },
        cutout: "60%",
      },
    });
  };
  
  const credentialsModalOpen = ref(false);
  const credentialsId = ref(null);
  const newCredentails = ref("");
  
  const openCredentialsModal = (credentials_id) => {
    credentialsModalOpen.value = true;
    credentialsId.value = credentials_id;
    const sale = sales.value.find((sale) =>
      sale.credentials.find((credential) => credential.id === credentials_id)
    );
    if (sale) {
      saleHistory.value = sale;
      newCredentails.value = sale.credentials[0].credentials;
    }
  };
  
  const closeCredentialsModal = () => {
    credentialsModalOpen.value = false;
    credentialsId.value = null;
  };
  
  const getLastHistory = (sale) => {
    const lastHistory = sale?.history?.sort(
      (a, b) => new Date(b?.created_at) - new Date(a?.created_at)
    )[0];
    return {
      status: lastHistory?.status?.name || "N/A",
      step: lastHistory?.step?.name || "N/A",
      created_at: lastHistory?.created_at || "N/A",
      message: lastHistory?.message || "N/A",
    };
  };
  
  const isCredentialsButtonEnabled = (item) => {
    if (!item.credentials?.length > 0) return false;
  
    const lastHistory = getLastHistory(item);
    const isBuyerRequest =
      lastHistory.step === "buyer_payment" && lastHistory.status === "reviewing";
    const isAdminReturned =
      lastHistory.step === "admin_review" && lastHistory.status === "returned";
    return isBuyerRequest || isAdminReturned;
  };
  
  const updateCredentials = async () => {
    const response = await axios.post(
      `/api/sale-credentials/update/${credentialsId.value}`,
      {
        credentials: newCredentails.value,
      }
    );
    if (response.status === 200 || response.status === 201) {
      closeCredentialsModal();
      newCredentails.value = "";
      addToast("Credentials updated", "success");
  
      await defineDatas();
    }
  };
  
  const imageUrl = ref("");
  const errors = ref({
    previewImage: "",
  });
  const hasUploadError = ref(false);
  
  const processImageFile = async (file) => {
    if (!file || !file.type.startsWith("image/")) {
      errors.value.previewImage = "Please select an image file";
      hasUploadError.value = true;
      addToast("Please select a valid image file", "error");
      return;
    }
  
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const result = await axios.post(
          `/api/users/${userInfo.value.id}/upload-image`,
          e.target.result
        );
        if ((result.status === 200 || result.status === 201) && result.data) {
          imageUrl.value = result.data;
          if (newCredentails.value.includes("![Image](Uploading Image...)")) {
            newCredentails.value = newCredentails.value.replace(
              "![Image](Uploading Image...)",
              `![Image](${imageUrl.value})`
            );
          } else {
            newCredentails.value += `\n\n![Image](${imageUrl.value})`;
          }
          addToast("Image uploaded successfully", "success");
        }
      } catch (error) {
        console.error("Error uploading image:", error);
        addToast("Failed to upload image", "error");
      }
    };
    reader.readAsDataURL(file);
  };
  
  const handlePaste = async (event) => {
    if (event.target?.files?.length > 0) {
      const file = event.target.files[0];
      newCredentails.value += `\n\n![Image](Uploading Image...)`;
      await processImageFile(file);
      return;
    }
  
    if (!event.clipboardData) return;
  
    if (
      event.clipboardData.types &&
      event.clipboardData.types.includes("text/plain")
    ) {
      return;
    }
  
    const items = event.clipboardData?.items;
    if (!items) return;
  
    newCredentails.value += `\n\n![Image](Uploading Image...)`;
  
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        event.preventDefault();
  
        const file = item.getAsFile();
        if (file) {
          await processImageFile(file);
        }
        break;
      }
    }
  };
  
  // Computed properties for search, pagination and sorting
  const filteredData = computed(() => {
    if (!searchQuery.value) {
      return rowData.value;
    }
  
    const query = searchQuery.value.toLowerCase();
    return rowData.value.filter(
      (item) =>
        item.listing?.toLowerCase().includes(query) ||
        item.buyer?.toLowerCase().includes(query) ||
        item.payment_method?.toLowerCase().includes(query) ||
        item.final_price?.toLowerCase().includes(query)
    );
  });
  
  const paginatedData = computed(() => filteredData.value);
  
  const totalFilteredItems = computed(() => filteredData.value.length);
  
  const balance = computed(() => {
    let totalUSD = 0;
    let totalUSDC = 0;
    let stripeSales = 0;
    let walletSales = 0;
    let stripeRevenue = 0;
    let walletRevenue = 0;
  
    sales.value.forEach((sale) => {
      totalUSD += parseFloat(sale.final_price);
      totalUSDC += parseFloat(sale.final_price);
      if (sale.payment_method.toLowerCase() === "stripe") {
        stripeSales++;
        stripeRevenue += parseFloat(sale.final_price);
      } else if (sale.payment_method.toLowerCase() === "wallet") {
        walletSales++;
        walletRevenue += parseFloat(sale.final_price);
      }
    });
  
    return {
      totalUSD: totalUSD.toFixed(2),
      totalUSDC: totalUSDC.toFixed(2),
      stripeSales,
      walletSales,
      stripeRevenue: stripeRevenue.toFixed(2),
      walletRevenue: walletRevenue.toFixed(2),
      totalSales: sales.value.length,
    };
  });
  
  const columns = [
    {
      label: "LISTING",
      key: "listing",
      type: "text",
      sortable: true,
    },
    {
      label: "METHOD",
      key: "payment_method",
      type: "text",
      sortable: true,
    },
    {
      label: "BUYER",
      key: "buyer",
      type: "text",
      sortable: true,
    },
    {
      label: "DATE",
      key: "created_at",
      type: "date",
      sortable: true,
    },
    {
      label: "AMOUNT",
      key: "final_price",
      type: "text",
      sortable: true,
    },
    {
      label: "ACTIONS",
      key: "actions",
      type: "actions",
      sortable: false,
    },
  ];
  
  // Handler functions for search, pagination and sorting
  const handleSearchQueryUpdate = (query) => {
    searchQuery.value = query;
    currentPage.value = 1;
  };
  
  const handlePageUpdate = (page) => {
    currentPage.value = page;
  };
  
  const handleItemsPerPageUpdate = (itemsPerPageValue) => {
    itemsPerPage.value = itemsPerPageValue;
    currentPage.value = 1;
  };
  
  const handleSort = (columnKey) => {
    if (sortBy.value === columnKey) {
      sortOrder.value = sortOrder.value === "asc" ? "desc" : "asc";
    } else {
      sortBy.value = columnKey;
      sortOrder.value = "asc";
    }
  
    // Sort the row data
    rowData.value.sort((a, b) => {
      let aVal = a[columnKey];
      let bVal = b[columnKey];
  
      const nullComparison = (aVal == null) - (bVal == null);
      if (nullComparison !== 0) return nullComparison;
  
      // Transform values based on column type
      const valueTransformers = {
        final_price: (val) => parseFloat(val.replace(/[^\d.-]/g, "")) || 0,
        created_at: (val) => new Date(val).getTime(),
        default: (val) => (typeof val === "string" ? val.toLowerCase() : val),
      };
  
      const transformer =
        valueTransformers[columnKey] || valueTransformers.default;
      aVal = transformer(aVal);
      bVal = transformer(bVal);
  
      // Compare values
      let comparison;
      if (aVal > bVal) {
        comparison = 1;
      } else if (aVal < bVal) {
        comparison = -1;
      } else {
        comparison = 0;
      }
      return sortOrder.value === "asc" ? comparison : -comparison;
    });
  
    // Reset to first page
    currentPage.value = 1;
  };
  
  const defineDatas = async () => {
    await fetchSales();
  
    const sortedSales = [...sales.value].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
  
    rowData.value = sortedSales.map((sale) => ({
      ...sale,
      listing_id: sale.listing.id,
      listing: sale.listing.name,
      buyer: sale.buyer ? sale.buyer.username : "External",
      created_at: sale.created_at,
      final_price: sale.final_price + " " + sale.currency,
      credentials: sale.credentials,
      license_id: sale.license_id,
      years_duration: sale.license_years_duration,
      custom_clauses: sale.license_custom_clauses,
    }));
    cardData.value = sortedSales.map((sale) => ({
      id: sale.id,
      title: sale.listing.name,
      rows: [
        {
          column: "Buyer",
          type: "text",
          value: sale.buyer ? sale.buyer.username : "External",
        },
        { column: "Date", type: "date", value: sale.created_at },
        {
          column: "Amount",
          type: "text",
          value: sale.final_price + " " + sale.currency,
        },
        {
          column: "Credentials",
          type: "text",
          value: sale?.credentials?.length > 0 ? "Yes" : "No",
        },
      ],
    }));
  };


const showListingLicenseModal = ref(false);
const licenseData = ref(null);

const handleViewLicense = (item) => {
  licenseData.value = {
    licenseId: item.license_id,
    yearsDuration: item.years_duration,
    customClauses: item.custom_clauses,
  };
  showListingLicenseModal.value = true;
};

const closeListingLicenseModal = () => {
  showListingLicenseModal.value = false;
  licenseData.value = null;
};
  
  onMounted(async () => {
    await defineDatas();
  });
  </script>
  
  <style scoped lang="postcss">
  .creator-profile__container {
    @apply relative overflow-hidden w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-20;
  }
  
  .creator-profile__header {
    @apply flex flex-col pb-4 text-center sm:text-left sm:flex-row items-center;
  }
  
  /* Add smooth transition for tooltip */
  .transition-transform {
    transition: transform 0.15s ease-in-out;
  }
  
  /* Dashboard Layout */
  .dashboard-grid {
    @apply grid grid-cols-1 lg:grid-cols-2 gap-6;
  }
  
  /* ===== SALES SUMMARY COMPONENT ===== */
  
  /* Main Container */
  .sales-summary {
    @apply p-4 rounded-xl bg-echoes-background-gray border border-echoes-border-light w-full space-y-4;
  }
  
  /* Header Section */
  .sales-summary-header {
    @apply flex justify-between items-start;
  }
  
  .header-content {
    @apply flex-1;
  }
  
  .sales-summary-title {
    @apply text-lg font-bold text-white;
  }
  
  .sales-summary-subtitle {
    @apply text-sm font-medium text-white/60;
  }
  
  .header-stats {
    @apply text-right;
  }
  
  .sales-summary-count {
    @apply text-2xl font-bold text-echoes-primary block;
  }
  
  .sales-summary-label {
    @apply text-xs font-medium text-white/60 uppercase;
  }
  
  /* Wallet and Total Section Side by Side */
  .wallet-total-section {
    @apply grid grid-cols-1 lg:grid-cols-2 gap-4;
  }
  
  .metric-card {
    @apply bg-echoes-background-black rounded-lg p-4 border border-echoes-border-light flex items-start transition-colors duration-200;
  }
  
  .metric-card:has(.metric-icon) {
    @apply gap-3;
  }
  
  .wallet-card {
    @apply bg-echoes-background-black hover:bg-echoes-background-black;
  }
  
  .metric-icon {
    @apply text-2xl flex-shrink-0 mt-1;
  }
  
  .metric-content {
    @apply flex-1 space-y-3;
  }
  
  .metric-header {
    @apply flex justify-between items-center;
  }
  
  .metric-title {
    @apply text-sm font-semibold text-white;
  }
  
  .metric-count {
    @apply text-xs text-white/60;
  }
  
  .metric-stats {
    @apply space-y-2;
  }
  
  .stat-row {
    @apply flex justify-between items-center;
  }
  
  .stat-label {
    @apply text-xs text-white/70;
  }
  
  .stat-value {
    @apply text-sm font-semibold;
  }
  
  /* Payment Method Colors */
  .stripe-value {
    @apply text-blue-400;
  }
  
  .wallet-value {
    @apply text-emerald-600;
  }
  
  /* Total Summary */
  .total-summary {
    @apply bg-echoes-background-black rounded-lg p-4 flex flex-col justify-center border border-echoes-border-light text-center;
  }
  
  .total-summary-header {
    @apply mb-2;
  }
  
  .total-summary-title {
    @apply text-sm font-semibold text-white uppercase tracking-wide;
  }
  
  .total-summary-value {
    @apply text-lg font-bold text-emerald-600;
  }
  
  .total-amount {
    @apply inline-block;
  }
  
  .total-separator {
    @apply text-white/60 mx-2;
  }
  
  /* ===== CHART COMPONENT ===== */
  
  /* Main Container */
  .chart-container {
    @apply p-4 rounded-xl bg-echoes-background-gray border border-echoes-border-light flex flex-col gap-4;
  }
  
  /* Header Section */
  .chart-header {
    @apply mb-4;
  }
  
  .chart-title {
    @apply text-lg font-bold text-white;
  }
  
  /* Chart Content */
  .chart-content {
    @apply relative h-64;
  }
  
  .sales-chart {
    @apply w-full h-full;
  }
  
  /* Receipt Details Section */
  .receipt-details-section {
    @apply mt-6;
  }
  
  .receipt-details-header {
    @apply mb-4;
  }
  
  .section-title {
    @apply text-xl font-bold text-white;
  }
  
  .section-subtitle {
    @apply text-sm text-white/60;
  }
  .overflow-y-auto {
    scrollbar-width: thin;
    scrollbar-color: #fe3e01 transparent;
  }
  
  .overflow-y-auto::-webkit-scrollbar {
    width: 6px;
  }
  
  .overflow-y-auto::-webkit-scrollbar-track {
    background: transparent;
  }
  
  .overflow-y-auto::-webkit-scrollbar-thumb {
    background-color: #fe3e01;
    border-radius: 3px;
    transition: background-color 0.3s ease;
  }
  
  .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background-color: #7c3aed;
  }
  
.tooltip {
  @apply absolute left-1/2 -translate-x-1/2 bottom-full px-3 py-2 text-xs font-semibold text-white/80 bg-echoes-background-black rounded-lg whitespace-normal w-max max-w-[300px];
  visibility: hidden;
  opacity: 0;
  transition: all 0.2s ease-in-out;
  z-index: 50;
}

.tooltip::after {
  content: '';
  @apply absolute left-1/2 -translate-x-1/2 -bottom-1 border-4 border-transparent border-t-gray-900;
}

.view-license-button:hover .tooltip {
  visibility: visible;
  opacity: 1;
}

.view-license-button {
  @apply text-white/80 hover:text-echoes-primary transition-colors relative bg-echoes-background-gray rounded-md p-0.5 px-1;
}
  </style>